import React, { useRef, useState } from 'react';
import { Upload, X, ImageIcon, Link as LinkIcon, Loader2, Crop } from 'lucide-react';
import toast from 'react-hot-toast';
import { compressImage, formatBytes } from '@/lib/image-utils';
import AdminCropModal from './AdminCropModal';

interface AdminImageUploadProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  shape?: 'rect' | 'square' | 'circle';
  className?: string;
  cropAspect?: number;
  enableCrop?: boolean;
}

const AdminImageUpload: React.FC<AdminImageUploadProps> = ({
  value,
  onChange,
  label = 'Image',
  shape = 'rect',
  className = '',
  cropAspect = 16 / 9,
  enableCrop = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'upload' | 'url'>(value && !value.startsWith('data:') ? 'url' : 'upload');
  const [dragOver, setDragOver] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [cropSrc, setCropSrc] = useState<string>('');
  const [showCrop, setShowCrop] = useState(false);

  const shouldCrop = enableCrop || shape === 'rect';

  const handleFileSelect = async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      toast.error(`"${file.name}" is not an image file`);
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`"${file.name}" exceeds 50MB limit (${formatBytes(file.size)})`);
      return;
    }

    setCompressing(true);
    try {
      const { dataUrl, originalSize, compressedSize } = await compressImage(file);
      toast.success(`Compressed: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`);

      if (shouldCrop) {
        setCropSrc(dataUrl);
        setShowCrop(true);
      } else {
        onChange(dataUrl);
      }
    } catch {
      toast.error(`Failed to process "${file.name}"`);
    } finally {
      setCompressing(false);
    }
  };

  const handleCropComplete = (croppedDataUrl: string): void => {
    onChange(croppedDataUrl);
    setShowCrop(false);
    setCropSrc('');
  };

  const handleCropCancel = (): void => {
    // Skip crop — use the full compressed image
    if (cropSrc) {
      onChange(cropSrc);
    }
    setShowCrop(false);
    setCropSrc('');
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(true);
  };

  const openCropExisting = (): void => {
    if (value) {
      setCropSrc(value);
      setShowCrop(true);
    }
  };

  const shapeClasses = {
    rect: 'w-full h-40 rounded-xl',
    square: 'w-32 h-32 rounded-xl',
    circle: 'w-28 h-28 rounded-full',
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40">
          {label}
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`flex items-center gap-1 px-2 py-1 text-[8px] font-bold uppercase tracking-wider rounded-md transition-all ${
              mode === 'upload'
                ? 'bg-black text-white'
                : 'text-black/30 hover:text-black/60'
            }`}
          >
            <Upload size={9} /> Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`flex items-center gap-1 px-2 py-1 text-[8px] font-bold uppercase tracking-wider rounded-md transition-all ${
              mode === 'url'
                ? 'bg-black text-white'
                : 'text-black/30 hover:text-black/60'
            }`}
          >
            <LinkIcon size={9} /> URL
          </button>
        </div>
      </div>

      {mode === 'url' ? (
        <div>
          <input
            value={value.startsWith('data:') ? '' : value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-3 bg-black/[0.03] border border-black/[0.06] rounded-xl text-sm font-medium outline-none focus:border-black/20 transition-all placeholder:text-black/20"
          />
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          onClick={() => !compressing && fileInputRef.current?.click()}
          className={`${shapeClasses[shape]} border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 transition-all ${
            compressing
              ? 'border-black/20 bg-black/[0.04]'
              : dragOver
                ? 'border-black/30 bg-black/[0.06]'
                : 'border-black/[0.08] bg-black/[0.02] hover:border-black/15 hover:bg-black/[0.04]'
          }`}
        >
          {compressing ? (
            <>
              <Loader2 size={18} className="text-black/30 animate-spin" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-black/30">
                Compressing...
              </span>
            </>
          ) : (
            <>
              <Upload size={18} className="text-black/20" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-black/25">
                Drop image or click
              </span>
              <span className="text-[8px] text-black/15">
                Any format — auto-compressed
              </span>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.tif,.tiff"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFileSelect(file);
              e.target.value = '';
            }}
            className="hidden"
          />
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="mt-3 relative inline-block">
          <img
            src={value}
            alt="Preview"
            className={`object-cover border border-black/[0.06] ${
              shape === 'circle' ? 'w-24 h-24 rounded-full' : shape === 'square' ? 'w-24 h-24 rounded-xl' : 'w-48 h-32 rounded-xl'
            }`}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
          >
            <X size={10} />
          </button>
          {shouldCrop && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openCropExisting();
              }}
              className="absolute -bottom-1.5 -right-1.5 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center hover:bg-black/80 transition-colors shadow-sm"
              title="Adjust crop"
            >
              <Crop size={10} />
            </button>
          )}
        </div>
      )}

      {!value && mode === 'url' && (
        <div className={`mt-3 ${shapeClasses[shape]} bg-black/[0.02] border border-dashed border-black/[0.08] flex items-center justify-center`}>
          <ImageIcon size={20} className="text-black/10" />
        </div>
      )}

      {/* Crop Modal */}
      <AdminCropModal
        imageSrc={cropSrc}
        open={showCrop}
        aspect={cropAspect}
        onComplete={handleCropComplete}
        onCancel={handleCropCancel}
      />
    </div>
  );
};

export default AdminImageUpload;
